import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Phone, PhoneCall, Check } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export const PhoneSettingsCard = ({ token }) => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/practitioner/phone-settings`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => setSettings(r.data))
      .catch(() => setSettings({ callback_enabled: false, phone_appointments_enabled: false }));
  }, [token]);

  const toggle = async (key) => {
    const value = !settings[key];
    const prev = settings;
    setSettings({ ...settings, [key]: value });
    try {
      await axios.put(`${API_URL}/practitioner/phone-settings`, { [key]: value }, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      setSettings(prev);
      alert("Impossible de mettre à jour ce paramètre");
    }
  };

  const Toggle = ({ active, onClick, testId }) => (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${active ? "bg-emerald-600" : "bg-slate-300"}`}
      aria-pressed={active}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${active ? "translate-x-5" : ""}`} />
    </button>
  );

  return (
    <div className="mt-6 bg-white rounded-xl border border-slate-100 p-6" data-testid="phone-settings-card">
      <div className="flex items-center gap-2 mb-1">
        <PhoneCall className="w-5 h-5 text-emerald-600" />
        <h3 className="font-semibold text-slate-900">Téléphone & rappels</h3>
      </div>
      <p className="text-sm text-slate-500 mb-5">
        Standard téléphonique saturé ? Laissez les patients demander un rappel ou réserver un rendez-vous téléphonique.
      </p>
      {!settings ? (
        <div className="h-16 animate-pulse bg-slate-50 rounded-lg" />
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <p className="font-medium text-slate-900 text-sm">Demandes de rappel</p>
              <p className="text-xs text-slate-500">Les patients peuvent demander à être rappelés par téléphone</p>
            </div>
            <Toggle active={settings.callback_enabled} onClick={() => toggle("callback_enabled")} testId="toggle-callback-enabled" />
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <p className="font-medium text-slate-900 text-sm">Rendez-vous téléphoniques</p>
              <p className="text-xs text-slate-500">Vos créneaux peuvent être réservés en consultation par téléphone</p>
            </div>
            <Toggle active={settings.phone_appointments_enabled} onClick={() => toggle("phone_appointments_enabled")} testId="toggle-phone-appointments" />
          </div>
        </div>
      )}
    </div>
  );
};

export const PractitionerCallbacksCard = ({ token }) => {
  const [items, setItems] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showHandled, setShowHandled] = useState(false);

  const load = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [cbRes, setRes] = await Promise.all([
        axios.get(`${API_URL}/practitioner/callbacks`, { headers }),
        axios.get(`${API_URL}/practitioner/phone-settings`, { headers }),
      ]);
      setItems(cbRes.data || []);
      setEnabled(!!setRes.data?.callback_enabled);
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const markHandled = async (id) => {
    try {
      await axios.patch(`${API_URL}/practitioner/callbacks/${id}/status?status=handled`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      load();
    } catch {
      alert("Erreur lors de la mise à jour");
    }
  };

  const pending = items.filter((i) => i.status === "pending");
  const handled = items.filter((i) => i.status === "handled").slice(0, 5);

  if (loading || (!enabled && items.length === 0)) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-100 p-6 mb-8" data-testid="practitioner-callbacks-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-semibold text-slate-900 flex items-center gap-2">
          <Phone className="w-5 h-5 text-emerald-600" />
          Demandes de rappel
          {pending.length > 0 && (
            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-bold" data-testid="pending-callbacks-count">
              {pending.length} en attente
            </span>
          )}
        </h2>
        {handled.length > 0 && (
          <button
            onClick={() => setShowHandled(!showHandled)}
            className="text-sm text-slate-500 hover:text-emerald-600 transition-colors"
            data-testid="toggle-handled-callbacks"
          >
            {showHandled ? "Masquer les traitées" : `Voir les traitées (${handled.length})`}
          </button>
        )}
      </div>

      {pending.length === 0 ? (
        <p className="text-slate-500 text-sm" data-testid="no-pending-callbacks">Aucune demande de rappel en attente.</p>
      ) : (
        <div className="space-y-3">
          {pending.map((cb) => (
            <div key={cb.id} className="border border-slate-100 rounded-xl p-4 flex items-center gap-4 flex-wrap bg-slate-50/50" data-testid={`callback-row-${cb.id}`}>
              <div className="flex-1 min-w-[180px]">
                <p className="font-semibold text-slate-900">{cb.patient_name}</p>
                <p className="text-sm text-slate-600">
                  {[cb.reason, cb.preferred_time].filter(Boolean).join(" · ")}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  Demandé le {new Date(cb.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })} à {new Date(cb.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
              {cb.phone && (
                <a
                  href={`tel:${cb.phone}`}
                  className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
                  data-testid={`call-patient-${cb.id}`}
                >
                  <PhoneCall className="w-4 h-4" /> {cb.phone}
                </a>
              )}
              <button
                onClick={() => markHandled(cb.id)}
                className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                data-testid={`mark-handled-${cb.id}`}
              >
                <Check className="w-4 h-4" /> Marquer traité
              </button>
            </div>
          ))}
        </div>
      )}

      {showHandled && handled.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
          {handled.map((cb) => (
            <div key={cb.id} className="flex items-center gap-3 text-sm text-slate-500">
              <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              <span className="font-medium text-slate-700">{cb.patient_name}</span>
              <span>{cb.reason || ""}</span>
              <span className="ml-auto text-xs">
                Traité le {cb.handled_at ? new Date(cb.handled_at).toLocaleDateString("fr-FR") : "—"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
